import React, { useState, useRef, useEffect } from 'react';
import { Camera, Plus, X, Image, Film, Loader2, Sparkles, Send } from 'lucide-react';
import { POST_TYPES } from './constants';
import { instagramPostsAPI, commentsAPI } from '../../services/api';
import { toast } from 'react-toastify';

// Comment with reply (Instagram-style)
const CommentItem = ({ comment: c, idx, sentimentBadge, commentSentiments, textPrimary, textSecondary, postId, darkMode, highlight, allUsernames, onReplyAdded }) => {
  const [showReply, setShowReply] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [highlighted, setHighlighted] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [localReplies, setLocalReplies] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const itemRef = useRef(null);
  const inputRef = useRef(null);

  const replies = [...(c.replies?.data || c.comments?.data || []), ...localReplies];

  useEffect(() => {
    if (highlight) {
      setHighlighted(true);
      setTimeout(() => itemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      setTimeout(() => setHighlighted(false), 2500);
    }
  }, [highlight]);

  const openReply = () => {
    const mention = `@${username} `;
    setReply(mention);
    setShowReply(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setReply(val);
    // Autocomplete @
    const match = val.match(/@(\w*)$/);
    if (match && match[1].length > 0 && allUsernames?.length) {
      const q = match[1].toLowerCase();
      setSuggestions(allUsernames.filter(u => u.toLowerCase().includes(q) && u.toLowerCase() !== q).slice(0, 5));
    } else {
      setSuggestions([]);
    }
  };

  const applySuggestion = (name) => {
    const newVal = reply.replace(/@(\w*)$/, `@${name} `);
    setReply(newVal);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await commentsAPI.reply(c.ig_comment_id || c.id, postId, reply.trim());
      toast.success('Resposta enviada!');
      setLocalReplies(prev => [...prev, { id: `local-${Date.now()}`, text: reply.trim(), message: reply.trim(), from: { username: 'você' }, timestamp: new Date().toISOString() }]);
      setReply('');
      setShowReply(false);
      setShowReplies(true);
    } catch (e) {
      toast.error('Erro ao responder comentário');
    }
    setSending(false);
  };

  const username = c.from?.username || c.from?.name || c.username || 'usuario';

  return (
    <div ref={itemRef} className={`py-2 px-1 -mx-1 rounded-lg transition-all duration-700 ${highlighted ? (darkMode ? 'bg-blue-500/15' : 'bg-blue-50') : ''}`}>
      <div className="flex gap-2.5">
        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold bg-gradient-to-br from-purple-500 to-pink-500 text-white`}>
          {username[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${textPrimary}`}>
            <span className="font-semibold">{username}</span>{' '}
            <span>{c.text || c.message}</span>
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-[11px] ${textSecondary}`}>{new Date(c.timestamp || c.created_time).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
            {sentimentBadge(commentSentiments[idx]?.sentimento)}
            <button onClick={openReply} className={`text-[11px] font-semibold ${textSecondary}`}>Responder</button>
            {replies.length > 0 && !showReplies && (
              <button onClick={() => setShowReplies(true)} className="text-[11px] font-semibold text-blue-500">
                Ver {replies.length} resposta{replies.length > 1 ? 's' : ''}
              </button>
            )}
            {showReplies && replies.length > 0 && (
              <button onClick={() => setShowReplies(false)} className="text-[11px] font-semibold text-blue-500">Ocultar respostas</button>
            )}
          </div>
          {showReply && (
            <div className="relative mt-2">
              <div className="flex items-center gap-2">
                <input ref={inputRef} value={reply} onChange={handleInputChange} onKeyDown={e => e.key === 'Enter' && handleReply()}
                  placeholder={`Responder @${username}...`}
                  className={`flex-1 text-sm px-3 py-1.5 rounded-full border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 placeholder-gray-400'}`} />
                <button onClick={handleReply} disabled={sending || !reply.trim()} className={`text-sm font-semibold ${reply.trim() ? 'text-blue-500' : 'text-blue-300'} disabled:opacity-50`}>
                  {sending ? <Loader2 size={14} className="animate-spin" /> : 'Publicar'}
                </button>
              </div>
              {suggestions.length > 0 && (
                <div className={`absolute left-0 top-full mt-1 rounded-lg shadow-lg border z-10 overflow-hidden ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                  {suggestions.map(s => (
                    <button key={s} onClick={() => applySuggestion(s)} className={`block w-full text-left px-3 py-1.5 text-sm ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}>
                      @{s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Replies */}
          {showReplies && replies.map(r => {
            const rUser = r.from?.username || r.from?.name || 'usuario';
            return (
              <div key={r.id} className="flex gap-2 mt-3">
                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
                  {rUser[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${textPrimary}`}>
                    <span className="font-semibold">{rUser}</span>{' '}
                    <span>{r.text || r.message}</span>
                  </p>
                  <span className={`text-[11px] ${textSecondary}`}>{new Date(r.timestamp || r.created_time).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Carousel Viewer
const CarouselViewer = ({ images }) => {
  const [idx, setIdx] = useState(0);
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <img src={images[idx]?.media_url} alt="" className="w-full h-full object-contain" />
      {images.length > 1 && (
        <>
          {idx > 0 && (
            <button onClick={() => setIdx(idx - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center text-gray-800 hover:bg-white">‹</button>
          )}
          {idx < images.length - 1 && (
            <button onClick={() => setIdx(idx + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center text-gray-800 hover:bg-white">›</button>
          )}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full transition ${i === idx ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Create/Edit Modal
export const CreateModal = ({ darkMode, form, setForm, editingPost, igInstances, uploading, onSubmit, onClose, fileRef, handleFiles, fluxos }) => {
  const cardBg = darkMode ? 'bg-zinc-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900';

  const selectedInstance = igInstances.find(i => i.id === form.instancia_ids?.[0]);
  const isFacebook = form.instancia_ids?.length === 1 && selectedInstance?.platform === 'facebook';
  const hasAnyFacebook = form.instancia_ids?.some(id => igInstances.find(i => i.id === id)?.platform === 'facebook');
  // Facebook pages don't support story via API
  const availableTypes = hasAnyFacebook ? POST_TYPES.filter(t => t.value !== 'story') : POST_TYPES;

  const getMediaSrc = (p) => p.startsWith('blob:') ? p : `${process.env.REACT_APP_API_URL}/instagram-posts/media/${encodeURIComponent(p)}`;
  const hasPreview = form.previews.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-4xl rounded-2xl ${cardBg} shadow-2xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-zinc-700">
          <h2 className={`text-lg font-bold ${textPrimary}`}>{editingPost ? 'Editar Postagem' : 'Nova Postagem'}</h2>
          <button onClick={onClose} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}><X className="w-5 h-5" /></button>
        </div>

        {/* Two columns */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left - Preview */}
          <div className={`w-1/2 flex flex-col items-center justify-center p-5 border-r ${darkMode ? 'border-zinc-700 bg-zinc-900/50' : 'border-gray-200 bg-gray-50'}`}>
            <input ref={fileRef} type="file" accept={POST_TYPES.find(t => t.value === form.tipo)?.accept}
              multiple={form.tipo === 'carousel'} onChange={handleFiles} className="hidden" />
            {hasPreview ? (
              <div className="w-full relative">
                {form.previews.length === 1 ? (
                  <div className="relative aspect-square bg-black rounded-xl overflow-hidden flex items-center justify-center">
                    {typeof form.previews[0] === 'string' && /\.(mp4|mov)$/i.test(form.previews[0]) ? (
                      <video src={getMediaSrc(form.previews[0])} controls className="w-full h-full object-contain" />
                    ) : (
                      <img src={getMediaSrc(form.previews[0])} alt="" className="w-full h-full object-contain" />
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {form.previews.map((p, i) => (
                      <div key={i} className="aspect-square bg-black rounded-lg overflow-hidden">
                        <img src={getMediaSrc(p)} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => fileRef.current?.click()}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                    Trocar mídia
                  </button>
                  <button onClick={() => setForm(prev => ({ ...prev, files: [], previews: [] }))}
                    className="py-2 px-3 rounded-lg text-sm font-medium text-red-500 transition hover:bg-red-500/10">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()}
                className={`w-full aspect-square border-2 border-dashed rounded-xl flex flex-col items-center justify-center transition ${darkMode ? 'border-zinc-600 hover:border-pink-500 text-gray-400' : 'border-gray-300 hover:border-pink-500 text-gray-500'}`}>
                <Plus className="w-10 h-10 mb-2" />
                <span className="text-sm font-medium">Selecionar {form.tipo === 'carousel' ? 'imagens (até 10)' : form.tipo === 'reel' ? 'vídeo' : 'arquivo'}</span>
              </button>
            )}
          </div>

          {/* Right - Form */}
          <div className="w-1/2 flex flex-col overflow-y-auto">
            <div className="p-5 space-y-4 flex-1">
              {/* Tipo */}
              <div>
                <label className={`text-sm font-medium ${textSecondary}`}>Tipo</label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {availableTypes.map(t => {
                    const Icon = t.icon;
                    return (
                      <button key={t.value} onClick={() => setForm(prev => ({ ...prev, tipo: t.value, files: [], previews: [] }))}
                        className={`p-2.5 rounded-xl text-center text-xs font-medium transition ${
                          form.tipo === t.value ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white' : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}>
                        <Icon className="w-4 h-4 mx-auto mb-0.5" />{t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Caption */}
              {form.tipo !== 'story' && (
                <div>
                  <label className={`text-sm font-medium ${textSecondary}`}>Legenda</label>
                  <textarea value={form.caption} onChange={e => setForm(prev => ({ ...prev, caption: e.target.value }))}
                    rows={6} maxLength={2200} placeholder="Escreva a legenda..."
                    className={`w-full mt-1 rounded-xl px-3 py-2 border text-sm resize-none ${inputBg}`} />
                  <p className={`text-xs text-right ${textSecondary}`}>{form.caption.length}/2200</p>
                </div>
              )}
              {/* Contas */}
              <div>
                <label className={`text-sm font-medium ${textSecondary}`}>Publicar em</label>
                <div className="mt-1 space-y-2">
                  {igInstances.map(i => {
                    const checked = form.instancia_ids?.includes(i.id);
                    return (
                      <label key={i.id} className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition ${
                        checked 
                          ? 'bg-gradient-to-r from-pink-500/10 to-purple-600/10 border border-pink-500/50' 
                          : darkMode ? 'bg-zinc-700 border border-zinc-600 hover:border-zinc-500' : 'bg-gray-50 border border-gray-200 hover:border-gray-300'
                      }`}>
                        <input type="checkbox" checked={checked}
                          onChange={() => setForm(prev => {
                            const newIds = checked
                              ? prev.instancia_ids.filter(id => id !== i.id)
                              : [...(prev.instancia_ids || []), i.id];
                            // Reset story type if any Facebook account selected
                            const hasFb = newIds.some(id => igInstances.find(x => x.id === id)?.platform === 'facebook');
                            const tipo = hasFb && prev.tipo === 'story' ? 'image' : prev.tipo;
                            return { ...prev, instancia_ids: newIds, tipo };
                          })}
                          className="rounded border-gray-300 text-pink-500 focus:ring-pink-500" />
                        <span className={`text-sm ${textPrimary}`}>
                          {i.platform === 'facebook' ? `📘 ${i.nome_instancia}` : `📸 @${i.instagram_username || i.nome_instancia}`}
                        </span>
                      </label>
                    );
                  })}
                  {igInstances.length === 0 && (
                    <p className={`text-sm ${textSecondary}`}>Nenhuma conta conectada</p>
                  )}
                </div>
              </div>
              {/* Agendamento */}
              <div>
                <label className={`text-sm font-medium ${textSecondary}`}>Agendar para (opcional)</label>
                <input 
                  type="datetime-local" 
                  value={form.scheduled_at} 
                  onChange={e => setForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                  className={`w-full mt-1 rounded-xl px-3 py-2 border text-sm ${inputBg}`} 
                />
              </div>
              {/* Comment Trigger */}
              {form.tipo !== 'story' && (
                <div className={`p-3 rounded-xl border ${form.comment_trigger_active 
                  ? 'border-pink-500/50 bg-gradient-to-r from-pink-500/5 to-purple-600/5' 
                  : darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.comment_trigger_active || false}
                      onChange={e => setForm(prev => ({ ...prev, comment_trigger_active: e.target.checked }))}
                      className="rounded border-gray-300 text-pink-500 focus:ring-pink-500" />
                    <span className={`text-sm font-medium ${textPrimary}`}>💬 Enviar DM automática por comentário</span>
                  </label>
                  {form.comment_trigger_active && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className={`text-xs font-medium ${textSecondary}`}>Palavra-chave (ex: QUERO)</label>
                        <input value={form.comment_trigger_keyword || ''} 
                          onChange={e => setForm(prev => ({ ...prev, comment_trigger_keyword: e.target.value }))}
                          placeholder="QUERO" 
                          className={`w-full mt-1 rounded-lg px-3 py-2 border text-sm ${inputBg}`} />
                      </div>
                      <div>
                        <label className={`text-xs font-medium ${textSecondary}`}>Mensagem da DM</label>
                        <textarea value={form.comment_trigger_dm || ''} 
                          onChange={e => setForm(prev => ({ ...prev, comment_trigger_dm: e.target.value }))}
                          rows={3} placeholder="Oi! Aqui está o que eu prometi no post..."
                          className={`w-full mt-1 rounded-lg px-3 py-2 border text-sm resize-none ${inputBg}`} />
                      </div>
                      <div>
                        <label className={`text-xs font-medium ${textSecondary}`}>Fluxo de atendimento após DM (opcional)</label>
                        <select value={form.comment_trigger_fluxo_id || ''} 
                          onChange={e => setForm(prev => ({ ...prev, comment_trigger_fluxo_id: e.target.value }))}
                          className={`w-full mt-1 rounded-lg px-3 py-2 border text-sm ${inputBg}`}>
                          <option value="">Nenhum — só envia a DM</option>
                          {(fluxos || []).map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                        </select>
                        <p className={`text-xs mt-1 ${textSecondary}`}>Se selecionado, o contato entra no fluxo quando responder a DM</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Actions */}
            <div className="flex gap-2 p-5 border-t border-gray-200 dark:border-zinc-700">
              <button onClick={onClose} className={`py-2.5 px-4 rounded-xl text-sm font-medium ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Cancelar</button>
              <button onClick={() => onSubmit(true)} disabled={uploading}
                className={`py-2.5 px-4 rounded-xl text-sm font-medium ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : '💾 Rascunho'}
              </button>
              <button onClick={() => onSubmit(false)} disabled={uploading}
                className="py-2.5 px-4 rounded-xl text-sm font-medium bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:opacity-90 transition">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : form.scheduled_at 
                  ? `📅 Agendar${form.instancia_ids?.length > 1 ? ` (${form.instancia_ids.length})` : ''}` 
                  : `🚀 Publicar${form.instancia_ids?.length > 1 ? ` (${form.instancia_ids.length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Details Modal (Instagram-style)
export const DetailsModal = ({ darkMode, post, detailsData, detailsLoading, getInstanceName, onClose, highlightComment }) => {
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const cardBg = darkMode ? 'bg-zinc-800' : 'bg-white';
  const [analyzing, setAnalyzing] = useState(false);
  const [sentimentData, setSentimentData] = useState(post.sentiment_summary || null);
  const [commentSentiments, setCommentSentiments] = useState({});

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      // Primeiro importa comentários pro banco
      await instagramPostsAPI.importComments(post.id);
      // Depois roda análise GPT
      const res = await instagramPostsAPI.analyzePost(post.id);
      if (res.data.summary) setSentimentData(res.data.summary);
      if (res.data.comments) {
        const map = {};
        res.data.comments.forEach(c => { if (c.index !== undefined) map[c.index] = c; });
        setCommentSentiments(map);
      }
    } catch (e) { console.error(e); }
    finally { setAnalyzing(false); }
  };

  const sentimentBadge = (sent) => {
    if (!sent) return null;
    const colors = {
      positivo: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
      neutro: 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-gray-400',
      negativo: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
    };
    const emoji = { positivo: '😊', neutro: '😐', negativo: '😞' };
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${colors[sent]}`}>{emoji[sent]}</span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className={`w-full max-w-6xl rounded-2xl overflow-hidden ${cardBg} shadow-2xl flex flex-col md:flex-row max-h-[92vh]`} onClick={e => e.stopPropagation()}>
        {/* Media */}
        <div className="md:w-[60%] bg-black flex items-center justify-center min-h-[500px] max-h-[92vh] relative">
          {detailsData?.children?.length > 0 ? (
            <CarouselViewer images={detailsData.children} />
          ) : detailsData?.media?.media_type === 'VIDEO' ? (
            <video src={detailsData.media.media_url} controls className="w-full h-full object-contain" poster={detailsData.media.thumbnail_url} />
          ) : detailsData?.media?.media_url ? (
            <img src={detailsData.media.media_url} alt="" className="w-full h-full object-contain" />
          ) : post.media_urls?.[0] && !/\.(mp4|mov)$/i.test(post.media_urls[0]) ? (
            <img src={`${process.env.REACT_APP_API_URL}/instagram-posts/media/${encodeURIComponent(post.media_urls[0])}`} alt="" className="w-full h-full object-contain" />
          ) : (
            <Film className="w-16 h-16 text-gray-600" />
          )}
          <span className="absolute top-4 left-4 text-base px-5 py-2.5 rounded-full bg-black/60 text-white backdrop-blur-sm flex items-center gap-2 font-semibold">
            {post.tipo === 'story' && <span className="w-3 h-3 rounded-full bg-gradient-to-br from-pink-500 to-yellow-500 animate-pulse" />}
            {POST_TYPES.find(t => t.value === post.tipo)?.label}
          </span>
        </div>

        {/* Info */}
        <div className="md:w-[40%] flex flex-col max-h-[92vh]">
          <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 p-[2px]">
                <div className={`w-full h-full rounded-full ${darkMode ? 'bg-zinc-800' : 'bg-white'} flex items-center justify-center`}>
                  <Camera className="w-4 h-4 text-pink-500" />
                </div>
              </div>
              <div>
                <p className={`text-base font-semibold ${textPrimary}`}>{getInstanceName(post.instancia_id) || 'Sem conta'}</p>
                <p className={`text-sm ${textSecondary}`}>
                  {post.published_at ? new Date(post.published_at + (post.published_at.endsWith?.('Z') ? '' : 'Z')).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
                    : post.scheduled_at ? `Agendado: ${new Date(post.scheduled_at + (post.scheduled_at.endsWith?.('Z') ? '' : 'Z')).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                    : 'Rascunho'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {detailsData?.media?.permalink && (
                <a href={detailsData.media.permalink} target="_blank" rel="noreferrer"
                  className={`p-2 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`} title={detailsData?.isFacebook ? 'Ver no Facebook' : 'Ver no Instagram'}>
                  {detailsData?.isFacebook ? (
                    <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  ) : (
                    <svg className="w-4 h-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  )}
                </a>
              )}
              <button onClick={onClose} className={`p-2 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Sentiment Summary */}
            {sentimentData && (
              <div className={`p-3 rounded-xl border ${darkMode ? 'border-zinc-700 bg-zinc-900/50' : 'border-gray-200 bg-gray-50'}`}>
                <p className={`text-xs font-semibold mb-2 ${textSecondary}`}>🧠 Análise de Sentimento</p>
                <div className="flex gap-3">
                  <div className="flex items-center gap-1"><span className="text-green-500">😊</span><span className={`text-sm font-semibold ${textPrimary}`}>{sentimentData.positive_pct}%</span></div>
                  <div className="flex items-center gap-1"><span className="text-gray-400">😐</span><span className={`text-sm font-semibold ${textPrimary}`}>{sentimentData.neutral_pct}%</span></div>
                  <div className="flex items-center gap-1"><span className="text-red-500">😞</span><span className={`text-sm font-semibold ${textPrimary}`}>{sentimentData.negative_pct}%</span></div>
                  <span className={`text-xs ml-auto ${textSecondary}`}>{sentimentData.total_analyzed} comentários</span>
                </div>
                {sentimentData.keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {sentimentData.keywords.slice(0, 8).map((k, i) => (
                      <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>{k}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {post.caption ? (
              <p className={`text-sm leading-relaxed ${textPrimary}`}>
                <span className="font-semibold">{getInstanceName(post.instancia_id)} </span>{post.caption}
              </p>
            ) : (
              <p className={`text-sm italic ${textSecondary}`}>Sem legenda</p>
            )}
            {detailsData?.comments?.length > 0 && (
              <div className={`space-y-2 pt-3 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
                {!sentimentData && post.status === 'published' && (
                  <button onClick={handleAnalyze} disabled={analyzing}
                    className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition ${
                      analyzing ? 'opacity-50' : 'hover:opacity-90'
                    } bg-gradient-to-r from-violet-500 to-pink-500 text-white`}>
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {analyzing ? 'Analisando...' : 'Analisar Sentimento'}
                  </button>
                )}
                {[...detailsData.comments].sort((a, b) => new Date(a.timestamp || a.created_time) - new Date(b.timestamp || b.created_time)).map((c, idx) => {
                  const allUsernames = [...new Set(detailsData.comments.flatMap(cm => [cm.from?.username, cm.from?.name, ...(cm.replies?.data || cm.comments?.data || []).map(r => r.from?.username || r.from?.name)]).filter(Boolean))];
                  return <CommentItem key={c.id} comment={c} idx={idx} sentimentBadge={sentimentBadge} commentSentiments={commentSentiments} textPrimary={textPrimary} textSecondary={textSecondary} postId={post.id} darkMode={darkMode} highlight={highlightComment && (c.ig_comment_id === highlightComment || c.id === highlightComment)} allUsernames={allUsernames} />;
                })}
              </div>
            )}
          </div>

          {/* Metrics */}
          {detailsLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-pink-500" /></div>
          ) : post.status === 'published' ? (
            <div className={`p-4 space-y-3 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-4">
                {detailsData?.isFacebook ? (
                  <>
                    <MetricIcon icon="heart" value={detailsData?.insights?.reactions} textPrimary={textPrimary} color="text-blue-500" />
                    <MetricIcon icon="comment" value={detailsData?.insights?.comments} textPrimary={textPrimary} />
                    <MetricIcon icon="send" value={detailsData?.insights?.shares} textPrimary={textPrimary} />
                    <MetricIcon icon="eye" value={detailsData?.insights?.impressions} textPrimary={textPrimary} className="ml-auto" />
                  </>
                ) : detailsData?.isStory ? (
                  <>
                    <MetricIcon icon="eye" value={detailsData?.insights?.reach} textPrimary={textPrimary} />
                    <MetricIcon icon="comment" value={detailsData?.insights?.replies} textPrimary={textPrimary} />
                    <MetricIcon icon="send" value={detailsData?.insights?.shares} textPrimary={textPrimary} />
                    <MetricIcon icon="heart" value={detailsData?.insights?.total_interactions} textPrimary={textPrimary} className="ml-auto" color="text-red-500" />
                  </>
                ) : (
                  <>
                    <MetricIcon icon="eye" value={detailsData?.insights?.views} textPrimary={textPrimary} />
                    <MetricIcon icon="heart" value={detailsData?.insights?.likes ?? detailsData?.media?.like_count} textPrimary={textPrimary} color="text-red-500" />
                    <MetricIcon icon="comment" value={detailsData?.insights?.comments ?? detailsData?.media?.comments_count} textPrimary={textPrimary} />
                    <MetricIcon icon="send" value={detailsData?.insights?.shares} textPrimary={textPrimary} />
                    <MetricIcon icon="bookmark" value={detailsData?.insights?.saved} textPrimary={textPrimary} className="ml-auto" />
                  </>
                )}
              </div>
              <div className={`grid grid-cols-4 gap-3 pt-3 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
                {detailsData?.isFacebook ? (
                  <>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${textPrimary}`}>{(detailsData?.insights?.impressions ?? 0).toLocaleString('pt-BR')}</p>
                      <p className={`text-sm ${textSecondary}`}>Impressões</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${textPrimary}`}>{(detailsData?.insights?.reach ?? 0).toLocaleString('pt-BR')}</p>
                      <p className={`text-sm ${textSecondary}`}>Alcance</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${textPrimary}`}>{(detailsData?.insights?.engaged_users ?? 0).toLocaleString('pt-BR')}</p>
                      <p className={`text-sm ${textSecondary}`}>Engajados</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${textPrimary}`}>{(detailsData?.insights?.clicks ?? 0).toLocaleString('pt-BR')}</p>
                      <p className={`text-sm ${textSecondary}`}>Cliques</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${textPrimary}`}>{(detailsData?.insights?.views ?? 0).toLocaleString('pt-BR')}</p>
                      <p className={`text-sm ${textSecondary}`}>Visualizações</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${textPrimary}`}>{(detailsData?.insights?.reach ?? 0).toLocaleString('pt-BR')}</p>
                      <p className={`text-sm ${textSecondary}`}>Alcance</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${textPrimary}`}>{(detailsData?.insights?.total_interactions ?? 0).toLocaleString('pt-BR')}</p>
                      <p className={`text-sm ${textSecondary}`}>Interações</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${textPrimary}`}>
                        {detailsData?.insights?.reach ? ((detailsData.insights.total_interactions / detailsData.insights.reach * 100).toFixed(1) + '%') : '0%'}
                      </p>
                      <p className={`text-sm ${textSecondary}`}>{detailsData?.isStory ? 'Atividade' : 'Engajamento'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className={`p-4 text-center border-t ${darkMode ? 'border-zinc-700' : 'border-gray-200'} ${textSecondary}`}>
              <p className="text-sm">Métricas disponíveis após publicação</p>
            </div>
          )}

          <div className={`px-4 py-3 text-xs ${textSecondary} ${darkMode ? 'border-t border-zinc-700' : 'border-t border-gray-100'}`}>
            {post.published_at ? new Date(post.published_at).toLocaleString('pt-BR') : `Criado em ${new Date(post.created_at).toLocaleString('pt-BR')}`}
            {post.error_message && <p className="text-red-500 mt-1">⚠️ {post.error_message}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// Metric icon helper
const MetricIcon = ({ icon, value, textPrimary, color, className = '' }) => {
  const icons = {
    heart: <svg className={`w-7 h-7 ${color || textPrimary}`} viewBox="0 0 24 24" fill={color ? 'currentColor' : 'none'} stroke={color ? 'none' : 'currentColor'} strokeWidth="2"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>,
    comment: <svg className={`w-7 h-7 ${color || textPrimary}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
    send: <svg className={`w-7 h-7 ${color || textPrimary}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>,
    bookmark: <svg className={`w-7 h-7 ${color || textPrimary}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>,
    eye: <svg className={`w-7 h-7 ${color || textPrimary}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  };
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {icons[icon]}
      <span className={`text-base font-semibold ${textPrimary}`}>{(value ?? 0).toLocaleString('pt-BR')}</span>
    </div>
  );
};
